import {Sku} from "./Sku";
import {getSKUS} from "../../config/config";

export class Price {
	/**
	 * @param {Object} options - 价格配置对象
	 * @param {number} options.week - 当前周期（周数）
	 * @param {number} options.price - 当前价格
	 * @param {string} options.paypal - PayPal 产品 ID
	 * @param {string} options.stripe - Stripe 产品 ID
	 * @param {boolean} [options.dynamicCycleSubscription=false] - 是否为周转月订阅
	 * @param {string} options.id - 唯一标识符（必需），如 "d0_1w_to_4w", "d61_4w"
	 * @param {number} [options.finalWeek] - 最终周期（对于周转月，如 1w_to_4w，最终是 4 周）
	 * @param {number} [options.finalPrice] - 最终价格（对于周转月，最终价格可能不同）
	 */
	constructor({
		week,
		price,
		paypal,
		stripe,
		dynamicCycleSubscription = false,
		id,
		finalWeek = null,
		finalPrice = null,
	}) {
		if (!id) {
			throw new Error("Price 必须提供唯一的 id 参数");
		}

		this.week = week;
		this.price = price;
		this.dynamicCycleSubscription = dynamicCycleSubscription;
		this.sku = new Sku(paypal, stripe);
		this.unit = "$";
		this.id = id;

		// 如果是周转月且没有指定最终周期/价格，则使用当前周期/价格
		this.finalWeek = finalWeek !== null ? finalWeek : week;
		this.finalPrice = finalPrice !== null ? finalPrice : price;
	}

	getPrice() {
		return `${this.unit}${this.price.toFixed(2)}`;
	}

	/**
	 * 使用 floor 方法格式化数值（向下取整）
	 * @param {number} value - 要格式化的数值
	 * @param {number} decimalPlaces - 小数位数
	 * @returns {string} 格式化后的字符串
	 */
	static formatWithFloor(value, decimalPlaces = 2) {
		if (value === null || value === undefined || isNaN(value)) {
			return "0.00";
		}
		const multiplier = Math.pow(10, decimalPlaces);
		return (
			Math.floor(parseFloat(value) * multiplier) / multiplier
		).toFixed(decimalPlaces);
	}

	getPerDayPrice() {
		const perDayValue = this.price / (this.week * 7);
		return `${this.unit}${Price.formatWithFloor(perDayValue, 2)}`;
	}

	getPerDayPriceWithoutUnit() {
		const perDayValue = this.price / (this.week * 7);
		return Price.formatWithFloor(perDayValue, 2);
	}

	// 获取最终价格的每日价格（基于 finalPrice 和 finalWeek）
	getFinalPerDayPrice() {
		const perDayValue = this.finalPrice / (this.finalWeek * 7);
		return `${this.unit}${Price.formatWithFloor(perDayValue, 2)}`;
	}

	// 获取最终价格的每日价格（不带单位）
	getFinalPerDayPriceWithoutUnit() {
		const perDayValue = this.finalPrice / (this.finalWeek * 7);
		return Price.formatWithFloor(perDayValue, 2);
	}

	// 获取唯一标识符，用于匹配原价
	getUniqueId() {
		return this.id;
	}

	// 获取周数的文本表示（单复数）
	getWeekText() {
		return this.week === 1 ? "week" : "weeks";
	}

	// 获取最终周期
	getFinalWeek() {
		return this.finalWeek;
	}

	// 获取最终价格
	getFinalPrice() {
		return `${this.unit}${this.finalPrice.toFixed(2)}`;
	}

	// 获取最终价格的原始数值
	getFinalPriceValue() {
		return this.finalPrice;
	}

	// 获取最终周期的文本表示（单复数）
	getFinalWeekText() {
		return this.finalWeek === 1 ? "week" : "weeks";
	}

	/**
	 * 从 price ID 中提取计划类型信息（如 "d61_1w_to_4w" -> "1w_to_4w"）
	 */
	static getPlanType(priceId) {
		const match = priceId.match(/^d\d+_(.+)$/);
		return match ? match[1] : priceId;
	}

	/**
	 * 根据计划类型从 SKU 列表中查找对应的 Price
	 */
	static findByPlanType(prices, planType) {
		return prices?.find(
			(v) => Price.getPlanType(v.getUniqueId()) === planType
		);
	}

	/**
	 * 从原价 SKU（discount=0）中查找对应的原价 Price 对象
	 */
	getOriginPrice() {
		const origin = this._getOriginPriceObject();
		if (origin) {
			return origin.getPrice();
		}
		return "";
	}

	/**
	 * 获取原价的每日价格
	 */
	getOriginPerDayPrice() {
		const origin = this._getOriginPriceObject();
		if (origin) {
			return origin.getPerDayPrice();
		}
		return "";
	}

	hasDiscount() {
		return this.getDiscountPercent() > 0;
	}

	getDiscountPercent() {
		const origin = this._getOriginPriceObject();
		if (!origin || origin.price === this.price || origin.price <= 0) {
			return 0;
		}

		const discounted = origin.price - this.price;
		return Math.floor((discounted / origin.price) * 100);
	}

	getDiscountPercentText() {
		const percent = this.getDiscountPercent();
		return percent > 0 ? `${percent}% OFF` : "";
	}

	/**
	 * 获取原价的 week 数
	 */
	getOriginWeek() {
		const origin = this._getOriginPriceObject();
		if (origin) {
			return origin.week;
		}
		return this.week;
	}

	/**
	 * 获取原价 Price 对象（从 discount=0 的 SKU 中查找）
	 * @private
	 */
	_getOriginPriceObject() {
		const planType = Price.getPlanType(this.getUniqueId());
		return Price.findByPlanType(getSKUS()?.[0]?.prices, planType);
	}

	/**
	 * 计算不同 SKU 之间的折扣金额（分）
	 * 先分别转换为分，再相减，避免浮点数精度问题
     * 给Server计算用
	 */
	getDiscountValueForServer() {
		const discounted = this.finalPrice * 100 - this.price * 100;
		return Math.round(discounted);
	}

	/**
	 * 获取带单位的折扣金额
	 * 使用分计算避免精度问题，最后转回美元
     * 前端展示用
	 */
	getDiscountValueWithUnit() {
		const origin = this._getOriginPriceObject();
		if (origin) {
			const discountedInCents = origin.price * 100 - this.price * 100;
			const discounted = Math.round(discountedInCents) / 100;
			return `${this.unit}${discounted.toFixed(2)}`;
		}
		return "$0.00";
	}
}
